import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';

const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' ? '/api' : `http://${h}:5001/api`;
};

const MONTH_NAMES  = ['','January','February','March','April','May','June',
                       'July','August','September','October','November','December'];
const curYear      = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => curYear - 2 + i);
const HEADER_H     = 52;

function isTradingDay(dateString) {
  if (!dateString) return false;
  const d = new Date(dateString + 'T00:00:00').getDay();
  return d >= 1 && d <= 5;
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function formatDateWithDay(dateString) {
  if (!dateString) return '--';
  const date = new Date(dateString + 'T00:00:00');
  const day  = date.toLocaleDateString('en-GB', { weekday: 'short' });
  const fmt  = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${day}, ${fmt}`;
}

const STARS = Array.from({ length: 80 }, (_, i) => ({
  id: i,
  left:  `${Math.random() * 100}%`,
  top:   `${Math.random() * 100}%`,
  delay: `${(Math.random() * 4).toFixed(2)}s`,
  size:  Math.random() > 0.8 ? 3 : 2,
}));

function InfoPill({ icon, value, label, color }) {
  return (
    <div style={{ ...S.pill, borderColor: color + '44' }}>
      <span style={{ ...S.pillDot, background: color }}>{icon}</span>
      <div>
        <div style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.1, color: '#fff' }}>{value}</div>
        <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</div>
      </div>
    </div>
  );
}

function ReversalCard({ reversal, index }) {
  return (
    <div style={S.card}>
      <div style={S.cardTop}>
        <span style={S.cardBadge}>🌀 #{index + 1}</span>
        <span style={S.cardDegBadge}>{reversal.matchedDegree}°</span>
      </div>
      <div style={S.cardDate}>{formatDateWithDay(reversal.date)}</div>
      <div style={S.cardDetails}>
        <div style={S.detailBlock}>
          <strong style={S.detailLabel}>SUN DEGREE</strong>
          <span style={S.detailValue}>{reversal.sunDegree?.toFixed(2)}°</span>
        </div>
        <div style={S.detailBlock}>
          <strong style={S.detailLabel}>REVERSAL POINT</strong>
          <span style={{ ...S.detailValue, color: '#f0c040' }}>{reversal.matchedDegree}°</span>
        </div>
      </div>
    </div>
  );
}

export default function GannReversal() {
  const [clock, setClock]                     = useState('--:--:--');
  const [currentYear, setCurrentYear]         = useState(curYear);
  const [currentMonth, setCurrentMonth]       = useState(null); // null = full year
  const [tradingOnly, setTradingOnly]         = useState(false);
  const [checkDate, setCheckDate]             = useState(todayISO());
  const [checkResult, setCheckResult]         = useState(null);
  const [checkLoading, setCheckLoading]       = useState(false);
  const [reversals, setReversals]             = useState([]);
  const [totalReversals, setTotalReversals]   = useState('--');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError]     = useState(false);
  const [nextReversal, setNextReversal]       = useState(null);
  const [statusType, setStatusType]           = useState('idle');
  const clockRef = useRef(null);

  const displayed    = tradingOnly ? reversals.filter((r) => isTradingDay(r.date)) : reversals;
  const tradingCount = reversals.filter((r) => isTradingDay(r.date)).length;

  useEffect(() => {
    const tick = () => {
      const t = new Date().toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' });
      setClock(t + ' IST');
    };
    tick();
    clockRef.current = setInterval(tick, 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  const loadCalendar = useCallback(async (year, month) => {
    setCalendarLoading(true);
    setCalendarError(false);
    setStatusType('loading');
    setReversals([]);
    try {
      const url = month
        ? `${getApiBase()}/sun-degree/period?year=${year}&month=${month}`
        : `${getApiBase()}/sun-degree/period?year=${year}`;
      const res  = await authFetch(url);
      const data = await res.json();
      setReversals(data.data || []);
      setTotalReversals(data.totalReversals ?? (data.data ? data.data.length : '--'));
      setStatusType('success');
    } catch {
      setCalendarError(true);
      setStatusType('error');
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => { loadCalendar(new Date().getFullYear(), null); }, []); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const res  = await authFetch(`${getApiBase()}/sun-degree/next?from=${todayISO()}`);
        const data = await res.json();
        setNextReversal(data.nextReversal || null);
      } catch { /* ignore */ }
    })();
  }, []);

  const handleMonthChange = (val) => {
    const m = val === '' ? null : Number(val);
    setCurrentMonth(m);
    loadCalendar(currentYear, m);
  };

  const handleYearSelect = (val) => {
    const y = Number(val);
    setCurrentYear(y);
    loadCalendar(y, currentMonth);
  };

  const handleCheckDate = async () => {
    if (!checkDate) return;
    setCheckLoading(true);
    setCheckResult(null);
    try {
      const res  = await authFetch(`${getApiBase()}/sun-degree/check?date=${checkDate}`);
      const data = await res.json();
      setCheckResult(data);
    } catch {
      setCheckResult({ error: true });
    } finally {
      setCheckLoading(false);
    }
  };

  const viewLabel = currentMonth
    ? `${MONTH_NAMES[currentMonth]} ${currentYear}`
    : `Full Year ${currentYear}`;

  const statusColor =
    statusType === 'loading' ? '#f59e0b' :
    statusType === 'success' ? '#10b981' :
    statusType === 'error'   ? '#ef4444' :
    'rgba(255,255,255,0.4)';

  const statusText =
    statusType === 'loading' ? `Loading reversal dates for ${viewLabel}…` :
    statusType === 'error'   ? 'Error loading data. Please try again.' :
    statusType === 'success' ? `${displayed.length} reversal${displayed.length !== 1 ? 's' : ''} for ${viewLabel}${tradingOnly ? ' · Trading days only' : ''} · Anchor: 20-Mar-2026 · Tolerance ±0.5°` :
    'Select a period and load.';

  const nextDateShort = nextReversal
    ? new Date(nextReversal.date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    : '--';

  return (
    <AppLayout pageBadge="Gann Reversal">
      <style>{`
        @keyframes srFadeIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={S.starfield}>
        {STARS.map((s) => (
          <div key={s.id} style={{
            ...S.star, left: s.left, top: s.top,
            animationDelay: s.delay, width: s.size, height: s.size,
          }} />
        ))}
      </div>

      <div style={{ ...S.shell, height: `calc(100vh - ${HEADER_H}px)` }}>

        <div style={S.controlsBar}>
          <div style={S.barLeft}>
            <span style={{ fontSize: '1.3rem' }}>🌀</span>
            <span style={S.pageTitle}>Gann Reversal</span>
            <span style={S.pageSub}>Sun Degree Trading</span>
            <span style={S.liveClock}>{clock}</span>
          </div>
          <div style={S.barRight}>
            <div style={S.selWrap}>
              <select
                value={currentMonth ?? ''}
                onChange={(e) => handleMonthChange(e.target.value)}
                style={S.sel}
              >
                <option value="" style={S.selOption}>Full Year</option>
                {MONTH_NAMES.slice(1).map((n, i) => (
                  <option key={i + 1} value={i + 1} style={S.selOption}>{n}</option>
                ))}
              </select>
              <span style={S.selArrow}>▾</span>
            </div>
            <div style={S.selWrap}>
              <select
                value={currentYear}
                onChange={(e) => handleYearSelect(e.target.value)}
                style={S.sel}
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y} style={S.selOption}>{y}</option>
                ))}
              </select>
              <span style={S.selArrow}>▾</span>
            </div>
            <div
              style={{ ...S.toggleTrack, background: tradingOnly ? 'rgba(102,126,234,0.25)' : 'rgba(255,255,255,0.06)' }}
              onClick={() => setTradingOnly((p) => !p)}
              title="Toggle trading days only"
            >
              <div style={{ ...S.toggleThumb, left: tradingOnly ? 'calc(100% - 26px)' : 3 }} />
              <span style={{ ...S.toggleLabel, paddingLeft: tradingOnly ? 10 : 28, color: tradingOnly ? '#fff' : 'rgba(255,255,255,0.45)' }}>
                {tradingOnly ? 'Mon–Fri' : 'All Days'}
              </span>
            </div>
            <input
              type="date"
              style={S.dateInput}
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
            />
            <button style={S.btnCheck} onClick={handleCheckDate} disabled={checkLoading}>
              {checkLoading ? '…' : '🔍 Check Date'}
            </button>
          </div>
        </div>


        {checkResult && !checkResult.error && (
          <div style={{
            ...S.checkResultBar,
            borderColor: checkResult.isReversal ? 'rgba(0,242,96,0.4)' : 'rgba(255,255,255,0.12)',
            background:  checkResult.isReversal ? 'rgba(0,242,96,0.08)' : 'rgba(255,255,255,0.03)',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', fontWeight: 600 }}>
              {formatDateWithDay(checkDate)}
            </span>
            <span style={{
              ...S.checkBadge,
              background: checkResult.isReversal
                ? 'linear-gradient(135deg,#00f260,#0575e6)'
                : 'rgba(255,255,255,0.15)',
            }}>
              {checkResult.isReversal ? '✓ REVERSAL DAY' : '✗ Normal Day'}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
              Sun: <strong style={{ color: '#f0c040' }}>{checkResult.sunDegree?.toFixed(2)}°</strong>
            </span>
            {checkResult.isReversal && (
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                Point: <strong style={{ color: '#00f260' }}>{checkResult.matchedDegree}°</strong>
              </span>
            )}
            <button style={S.btnClose} onClick={() => setCheckResult(null)}>✕</button>
          </div>
        )}
        {checkResult?.error && (
          <div style={{ ...S.checkResultBar, borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.06)' }}>
            <span style={{ color: '#fca5a5', fontSize: '0.82rem' }}>⚠️ Error checking date. Please try again.</span>
            <button style={S.btnClose} onClick={() => setCheckResult(null)}>✕</button>
          </div>
        )}

        <div style={S.infoRow}>
          <InfoPill icon="🌀" value={totalReversals}   label="Total Reversals" color="#667eea" />
          <InfoPill icon="📅" value={tradingCount}      label="Trading Days"    color="#f59e0b" />
          <InfoPill icon="✅" value={displayed.length}  label="Showing"         color="#10b981" />
          <InfoPill icon="⏰" value={nextDateShort}     label="Next Reversal"   color="#f0c040" />
          <InfoPill icon="📆" value={nextReversal ? `${nextReversal.daysAway}d` : '--'} label="Days Away" color="#00f260" />
          <div style={S.stripDivider} />
          <div style={S.aboutChip}>
            <span style={{ color: '#f0c040', fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>ℹ️</span>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
              Swing reversal dates based on Sun's ecliptic degree. Anchor{' '}
              <strong style={{ color: 'rgba(255,255,255,0.85)' }}>20-Mar-2026</strong> (Vernal Equinox) · 36 points · ±0.5° tolerance
            </span>
          </div>
        </div>

        <div style={{ ...S.statusBar, color: statusColor, borderColor: statusColor + '33' }}>
          {statusText}
        </div>

        <div style={S.cardsArea}>
          {calendarLoading && !reversals.length ? (
            <div style={S.centeredMsg}>
              <div style={S.loaderRing} />
              <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                Loading reversal dates…
              </p>
            </div>
          ) : calendarError && !reversals.length ? (
            <div style={S.centeredMsg}>
              <div style={{ fontSize: '2.5rem', opacity: 0.35 }}>⚠️</div>
              <p style={{ marginTop: 12, color: '#fca5a5', fontSize: '0.82rem' }}>Error loading data. Please try again.</p>
            </div>
          ) : displayed.length === 0 && statusType === 'success' ? (
            <div style={S.centeredMsg}>
              <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>📅</div>
              <p style={{ marginTop: 12, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                No reversal dates{tradingOnly ? ' on trading days' : ''} found for this period.
              </p>
            </div>
          ) : (
            <div style={S.cardsGrid}>
              {displayed.map((reversal, i) => (
                <ReversalCard key={reversal.date || i} reversal={reversal} index={i} />
              ))}
            </div>
          )}
        </div>

      </div>
    </AppLayout>
  );
}

const S = {
  starfield: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
    background: 'linear-gradient(135deg,#0d0d1a 0%,#1a1a2e 50%,#16213e 100%)',
  },
  star: {
    position: 'absolute', background: '#fff', borderRadius: '50%',
    animation: 'twinkle 3s infinite',
  },
  shell: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    padding: '10px 14px 12px',
    marginTop: HEADER_H,
    gap: 8,
    color: '#fff',
    fontFamily: "'Poppins','Segoe UI',sans-serif",
  },
  controlsBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '10px 16px',
  },
  barLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
  barRight: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  pageTitle: { fontSize: '1rem', fontWeight: 700, color: '#fff' },
  pageSub:   { fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' },
  liveClock: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    padding: '3px 10px', borderRadius: 6,
    fontSize: '0.75rem', fontWeight: 600, color: '#fff',
  },
  selWrap: { position: 'relative' },
  sel: {
    appearance: 'none',
    padding: '7px 30px 7px 12px',
    fontSize: '0.82rem', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)',
    background: '#1e2340', color: '#fff',
    cursor: 'pointer', minWidth: 120, outline: 'none',
    fontFamily: 'inherit',
  },
  selOption: { background: '#1e2340', color: '#fff' },
  selArrow: {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.4)', pointerEvents: 'none', fontSize: '0.75rem',
  },
  toggleTrack: {
    position: 'relative', height: 30, width: 90,
    borderRadius: 15, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center',
    overflow: 'hidden', flexShrink: 0,
    transition: 'background 0.3s',
  },
  toggleThumb: {
    position: 'absolute', top: 3,
    width: 24, height: 24,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    borderRadius: '50%',
    transition: 'left 0.25s', zIndex: 0,
    boxShadow: '0 2px 6px rgba(102,126,234,0.5)',
  },
  toggleLabel: {
    position: 'relative', zIndex: 1,
    fontSize: '0.63rem', fontWeight: 600, letterSpacing: 0.3,
    pointerEvents: 'none', userSelect: 'none',
    transition: 'color 0.3s, padding-left 0.25s',
    whiteSpace: 'nowrap',
  },
  dateInput: {
    padding: '6px 10px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 7, color: '#fff',
    fontSize: '0.8rem', outline: 'none', colorScheme: 'dark',
  },
  btnCheck: {
    background: 'linear-gradient(135deg,#00f260,#0575e6)',
    border: 'none', borderRadius: 7,
    color: '#fff', padding: '6px 14px',
    fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  checkResultBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    border: '1px solid', borderRadius: 8,
    padding: '8px 14px',
    animation: 'srFadeIn 0.3s ease',
  },
  checkBadge: {
    padding: '3px 12px', borderRadius: 20,
    fontSize: '0.72rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase',
  },
  btnClose: {
    marginLeft: 'auto',
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.6)', width: 24, height: 24,
    borderRadius: '50%', cursor: 'pointer', fontSize: '0.7rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  infoRow: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12, padding: '10px 16px',
  },
  pill: {
    display: 'flex', alignItems: 'center', gap: 8,
    border: '1px solid', borderRadius: 10,
    padding: '6px 14px', background: 'rgba(255,255,255,0.04)', flexShrink: 0,
  },
  pillDot: {
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.9rem', flexShrink: 0,
  },
  stripDivider: {
    width: 1, alignSelf: 'stretch', minHeight: 32,
    background: 'rgba(255,255,255,0.09)', flexShrink: 0,
  },
  aboutChip: { display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 180 },
  statusBar: {
    flexShrink: 0,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid', borderRadius: 8,
    padding: '7px 14px', fontSize: '0.8rem', textAlign: 'center',
    transition: 'color 0.3s, border-color 0.3s',
  },
  cardsArea: {
    flex: 1, minHeight: 0,
    overflowY: 'auto', overflowX: 'hidden',
    borderRadius: 12,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 10,
    padding: '4px 2px 8px',
  },
  centeredMsg: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', minHeight: 200,
  },
  loaderRing: {
    width: 44, height: 44, borderRadius: '50%',
    border: '3px solid rgba(102,126,234,0.2)',
    borderTopColor: '#667eea',
    animation: 'spin 0.9s linear infinite',
  },
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(240,192,64,0.2)',
    borderRadius: 14, padding: '12px 14px',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
  },
  cardTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 8, paddingBottom: 8,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  cardBadge: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    color: '#fff', padding: '3px 12px',
    borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
  },
  cardDegBadge: {
    background: 'linear-gradient(135deg,#f0c040,#ff6b6b)',
    color: '#fff', padding: '3px 10px',
    borderRadius: 20, fontSize: '0.75rem', fontWeight: 700,
  },
  cardDate: { fontSize: '0.95rem', fontWeight: 700, color: '#f0c040', marginBottom: 8 },
  cardDetails: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  detailBlock: { background: 'rgba(0,0,0,0.2)', padding: '7px 10px', borderRadius: 8 },
  detailLabel: {
    display: 'block', fontSize: '0.6rem', textTransform: 'uppercase',
    letterSpacing: 1, color: 'rgba(255,255,255,0.38)', marginBottom: 4, fontWeight: 600,
  },
  detailValue: { color: '#fff', fontSize: '0.85rem', fontWeight: 600 },
};
